import { AudioChunkAccumulator } from "./AudioChunkAccumulator";
import workletSource from "./pcm-capture.worklet.js?raw";

export const CAPTURE_SAMPLE_RATE = 24000;
const SCRIPT_PROCESSOR_BUFFER = 8192;
/** Inaudible output that stops Chrome moving the context to a throttled fake sink; see the worklet. */
const KEEP_AWAKE_LEVEL = 1e-6;

export type PcmCaptureKind = "audio-worklet" | "script-processor";

export interface PcmCapture {
  readonly sampleRate: number;
  /** Null until the capture node is connected. */
  readonly kind: PcmCaptureKind | null;
  resume: () => void;
  /** Stops capture and closes the AudioContext. Never stops the stream's tracks. */
  stop: () => void;
}

/**
 * Streams the microphone as 24 kHz mono PCM16. Prefers an AudioWorklet so a
 * busy or throttled page does not drop audio, and keeps the context on the
 * real output device so its clock is not slowed in background windows; falls
 * back to ScriptProcessorNode where AudioWorklet is unavailable.
 */
export const startPcmCapture = (
  stream: MediaStream,
  onPcm: (pcm: Int16Array) => void,
): PcmCapture => {
  const context = new window.AudioContext({ sampleRate: CAPTURE_SAMPLE_RATE });
  const source = context.createMediaStreamSource(stream);
  let stopped = false;
  let kind: PcmCaptureKind | null = null;
  let scriptProcessor: ScriptProcessorNode | null = null;
  let worklet: AudioWorkletNode | null = null;

  const useScriptProcessor = (): void => {
    scriptProcessor = context.createScriptProcessor(
      SCRIPT_PROCESSOR_BUFFER,
      1,
      1,
    );
    scriptProcessor.onaudioprocess = (event): void => {
      const output = event.outputBuffer.getChannelData(0);
      for (let index = 0; index < output.length; index++)
        output[index] = index % 2 === 0 ? KEEP_AWAKE_LEVEL : -KEEP_AWAKE_LEVEL;
      if (!stopped)
        onPcm(AudioChunkAccumulator.fromAudioProcessingEventToPCM16(event));
    };
    source.connect(scriptProcessor);
    scriptProcessor.connect(context.destination);
    kind = "script-processor";
  };

  if (context.audioWorklet && typeof AudioWorkletNode !== "undefined") {
    // A blob URL works under any deployment base path and in every browser
    // with AudioWorklet (data: URLs are not accepted everywhere).
    const workletUrl = URL.createObjectURL(
      new Blob([workletSource], { type: "text/javascript" }),
    );
    context.audioWorklet
      .addModule(workletUrl)
      .finally(() => URL.revokeObjectURL(workletUrl))
      .then(() => {
        if (stopped) return;
        worklet = new AudioWorkletNode(context, "pcm-capture", {
          numberOfInputs: 1,
          numberOfOutputs: 1,
          outputChannelCount: [1],
          channelCount: 1,
          channelCountMode: "explicit",
        });
        worklet.port.onmessage = (event: MessageEvent<Int16Array>): void => {
          if (!stopped) onPcm(event.data);
        };
        source.connect(worklet);
        // Connected so the graph pulls the node; its output is silence.
        worklet.connect(context.destination);
        kind = "audio-worklet";
      })
      .catch(() => {
        if (!stopped) useScriptProcessor();
      });
  } else {
    useScriptProcessor();
  }

  return {
    sampleRate: context.sampleRate,
    get kind() {
      return kind;
    },
    resume: (): void => {
      void context.resume().catch(() => {});
    },
    stop: (): void => {
      if (stopped) return;
      stopped = true;
      if (scriptProcessor) {
        scriptProcessor.onaudioprocess = null;
        scriptProcessor.disconnect();
      }
      if (worklet) {
        worklet.port.onmessage = null;
        worklet.port.close();
        worklet.disconnect();
      }
      source.disconnect();
      void context.close().catch(() => {});
    },
  };
};
